import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  FormProvider,
  type SubmitErrorHandler,
  type UseFormReturn,
  useForm,
  type ValidationMode,
} from 'react-hook-form';
import { type AnyObject, type InferType, type ObjectSchema } from 'yup';
import { createYupAugmentedResolver } from '../resolver/createYupAugmentedResolver.ts';
import { FormAugmentedContextProvider } from './augmented/FormAugmentedContext.tsx';
import { castValuesWithLivePaths } from './castValuesWithLivePaths.ts';
import { RegisterCoordinatedForm } from './coordinator/RegisterCoordinatedForm.tsx';
import { createFrozenFormStateProxy } from './debug/createFrozenFormStateProxy.ts';
import { Form, type IFormProps } from './Form.tsx';
import { FormPreventNavigation } from './prevent/FormPreventNavigation.tsx';

export interface IFormHandlerProps<
  T extends AnyObject,
  TSchema extends ObjectSchema<T> = ObjectSchema<T>,
> extends IFormProps {
  schema: TSchema;
  initialValues?: any;
  onSubmit: (
    data: InferType<TSchema>,
    event?: React.BaseSyntheticEvent,
    methods?: UseFormReturn<any, any, any>,
  ) => any | Promise<any>;
  /**
   * Gestisce l'errore quando il submit non è valido (ci sono degli errori)
   */
  onInvalid?: SubmitErrorHandler<InferType<TSchema>>;
  children: React.ReactNode;
  validationContext?: any;
  mode?: keyof ValidationMode;
  disableReinitialize?: boolean;
  /**
   * Se true, esegue `schema.cast()` sugli initialValues prima di passarli
   * a react-hook-form. Utile per risolvere valori computed (parentWhen transforms)
   * al primo render. Disabilitato di default perché il cast applica i default
   * di Yup (es. crea oggetti vuoti per yup.object()) che possono sovrascrivere
   * dati reali provenienti dal DB.
   */
  castInitialValues?: boolean;
}

export function FormHandler<
  T extends AnyObject,
  TSchema extends ObjectSchema<T>,
>(props: IFormHandlerProps<T, TSchema>) {
  const {
    schema,
    onSubmit,
    onInvalid,
    onBlur,
    initialValues,
    hideVisualFeedback,
    children,
    validationContext,
    mode,
    disableReinitialize,
    castInitialValues,
    ...formProps
  } = props;

  // Ref stabile creato PRIMA del resolver (che lo cattura in closure).
  // Viene popolato dopo useForm con methods.control.
  const controlRef = useRef<UseFormReturn['control'] | null>(null);

  const resolver = useMemo(
    () =>
      createYupAugmentedResolver(schema as ObjectSchema<AnyObject>, {
        controlRef,
      }),
    [schema],
  );

  // Ref per avere il validationContext corrente senza aggiungerlo alle deps
  // di initialCastValues: evita reset spurii del form quando il context
  // cambia referenza (e.g. durante il submit) senza che initialValues sia cambiato.
  const validationContextRef = useRef(validationContext);
  validationContextRef.current = validationContext;

  // Calcoliamo i valori iniziali con schema.cast() per risolvere
  // tutti i campi calcolati (parentWhen transforms) prima del mount.
  // Senza questo, i computed avrebbero valore undefined al primo render.
  const initialCastValues = useMemo(() => {
    if (!castInitialValues) return initialValues;
    if (!initialValues) return initialValues;
    try {
      return castValuesWithLivePaths(
        schema as ObjectSchema<AnyObject>,
        initialValues,
        validationContextRef.current,
      );
    } catch {
      return initialValues;
    }
  }, [schema, initialValues, castInitialValues]);

  const methods = useForm({
    defaultValues: initialCastValues,
    // Prima dell'invio (submit)
    mode: mode ?? 'onBlur',
    // Dopo l'invio (submit)
    reValidateMode: 'onChange',
    // criteriaMode: 'all',
    shouldUnregister: false,
    resolver,
    context: validationContext,
  });

  controlRef.current = methods.control;

  // Reinizializza il form quando initialCastValues cambia DOPO il mount.
  // Al primo mount useForm ha già i defaultValues corretti; chiamare reset()
  // sovrascrive i valori che useFieldArray.replace() setta nei propri effect,
  // creando una race condition (i child effect girano prima del parent effect).
  const isFirstMount = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: loop infinito evitato
  useEffect(() => {
    if (disableReinitialize) return;
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    methods.reset(initialCastValues);
  }, [initialCastValues]);

  const handleReset = useCallback(
    () => methods.reset(initialCastValues),
    [initialCastValues, methods],
  );

  /**
   * Gestisce l'errore quando il submit non è valido (ci sono degli errori di
   * validazione). Se `showVisualFeedback` è true, fa lo scroll verso l'alto
   * della pagina.
   */
  const handleInvalid: SubmitErrorHandler<InferType<ObjectSchema<T>>> =
    useCallback(
      (errors, event) => {
        onInvalid?.(errors, event);
        if (!hideVisualFeedback) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
      },
      [hideVisualFeedback, onInvalid],
    );

  // Passo anche i methods all'onSubmit dando la possibilità di chiamarli anche
  // durante l'invio del form (e.g. per resettare il form.)
  const handleOnSubmit = useCallback(
    (data: any, e: any) => onSubmit(data, e, methods),
    [methods, onSubmit],
  );

  // Stabilizziamo la referenza di formState passata a FormProvider.
  // useForm() ri-renderizza FormHandler ad ogni cambio di stato (reRenderRoot: true)
  // e muta methods.formState ad un nuovo Proxy. FormProvider include formState
  // nei deps del suo useMemo -> nuovo context -> propagateContextChange_eager (~17ms)
  // -> unsubscribe/resubscribe di tutti i consumatori (~25ms).
  //
  // Ma nel nostro codice nessuno legge formState da `useFormContext()`: tutti usano
  // `useFormState()` che sottoscrive via HookFormControlContext (stabile).
  // Quindi possiamo congelare la referenza di formState per FormProvider.
  //
  // NOTE: ⚠️ un Proxy logga un warning se qualcuno legge proprietà reattive
  // (errors, isDirty, isValid, etc.) da questo formState congelato, dato che
  // il valore non si aggiornerà mai.
  const stableFormState = useRef(
    process.env.NODE_ENV === 'development'
      ? createFrozenFormStateProxy(methods.formState)
      : methods.formState,
  );

  return (
    <FormProvider {...methods} formState={stableFormState.current}>
      <RegisterCoordinatedForm id={props.id} methods={methods} />
      <FormAugmentedContextProvider
        schema={schema as ObjectSchema<AnyObject>}
        validationContext={validationContext}
        initialValues={initialCastValues}
      >
        <Form
          onSubmit={handleOnSubmit}
          onInvalid={handleInvalid}
          onReset={handleReset}
          onBlur={onBlur}
          {...formProps}
        >
          {!hideVisualFeedback && (
            <FormPreventNavigation currentFormId={props.id} />
          )}
          {children}
        </Form>
      </FormAugmentedContextProvider>
    </FormProvider>
  );
}
